import React, { useState, useEffect, useCallback } from 'react';
import { Badge, Button, Popover, List, Typography, Space, Empty, Tooltip, Tag } from 'antd';
import { BellOutlined, BellFilled } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import api from '../api';

dayjs.extend(relativeTime);

export default function AlertBell() {
  const [alerts, setAlerts]     = useState([]);
  const [open, setOpen]         = useState(false);
  const [loading, setLoading]   = useState(false);
  const navigate = useNavigate();

  const unreadCount = alerts.filter((a) => !a.IsRead).length;

  const fetchAlerts = useCallback(async () => {
    try {
      const res = await api.get('/api/alerts');
      setAlerts(res.data);
    } catch {
      // non-critical
    }
  }, []);

  // Fetch on mount and poll every 60 s for badge count
  useEffect(() => {
    fetchAlerts();
    const id = setInterval(fetchAlerts, 60000);
    return () => clearInterval(id);
  }, [fetchAlerts]);

  // Re-fetch when popover opens
  useEffect(() => {
    if (open) {
      setLoading(true);
      fetchAlerts().finally(() => setLoading(false));
    }
  }, [open, fetchAlerts]);

  const handleMarkRead = async (alert) => {
    if (!alert.IsRead) {
      await api.post(`/api/alerts/${alert.ID}/read`).catch(() => {});
      setAlerts((prev) => prev.map((a) => a.ID === alert.ID ? { ...a, IsRead: 1 } : a));
    }
    if (alert.LinkPath) {
      setOpen(false);
      navigate(alert.LinkPath);
    }
  };

  const handleMarkAllRead = async () => {
    await api.post('/api/alerts/read-all').catch(() => {});
    setAlerts((prev) => prev.map((a) => ({ ...a, IsRead: 1 })));
  };

  const content = (
    <div style={{ width: 380 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <Typography.Text strong>Notifications</Typography.Text>
        {unreadCount > 0 && (
          <Button type="link" size="small" onClick={handleMarkAllRead} style={{ padding: 0 }}>
            Mark all as read
          </Button>
        )}
      </div>

      {alerts.length === 0 ? (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No notifications" style={{ margin: '16px 0' }} />
      ) : (
        <List
          loading={loading}
          dataSource={alerts.slice(0, 10)}
          style={{ maxHeight: 420, overflowY: 'auto' }}
          renderItem={(alert) => (
            <List.Item
              style={{
                padding: '8px 4px',
                background: alert.IsRead ? 'transparent' : '#e6f4ff',
                borderRadius: 4,
                marginBottom: 2,
                cursor: alert.LinkPath ? 'pointer' : 'default',
              }}
              onClick={() => handleMarkRead(alert)}
            >
              <div style={{ width: '100%' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 2 }}>
                  <Typography.Text style={{ fontSize: 12, color: '#8c8c8c' }}>
                    {dayjs(alert.CreatedAt).fromNow()}
                  </Typography.Text>
                  {!alert.IsRead && <Tag color="blue" style={{ fontSize: 11, lineHeight: '16px' }}>New</Tag>}
                </div>
                <Typography.Text style={{ fontSize: 13 }}>{alert.Text}</Typography.Text>
                {alert.LinkPath && (
                  <div>
                    <Typography.Link style={{ fontSize: 12 }}>View →</Typography.Link>
                  </div>
                )}
              </div>
            </List.Item>
          )}
        />
      )}

      <div style={{ borderTop: '1px solid #f0f0f0', marginTop: 8, paddingTop: 8, textAlign: 'center' }}>
        <Button
          type="link"
          size="small"
          style={{ padding: 0 }}
          onClick={() => { setOpen(false); navigate('/alerts'); }}
        >
          View all notifications →
        </Button>
      </div>
    </div>
  );

  return (
    <Popover
      content={content}
      trigger="click"
      open={open}
      onOpenChange={setOpen}
      placement="bottomRight"
      arrow={false}
    >
      <Tooltip title="Notifications">
        <Badge count={unreadCount} size="small" offset={[-2, 2]}>
          <Button
            type="text"
            icon={unreadCount > 0 ? <BellFilled style={{ color: '#fff', fontSize: 18 }} /> : <BellOutlined style={{ color: '#fff', fontSize: 18 }} />}
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          />
        </Badge>
      </Tooltip>
    </Popover>
  );
}
